import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { checkRankUp } from '../utils/points';
import { ArrowLeft, Check, X, BookOpen, Award, Bookmark, BookmarkCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const QuizView = ({ user, onLogout }) => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [savedFlashcards, setSavedFlashcards] = useState({});
  const [savingFlashcard, setSavingFlashcard] = useState(null);

  useEffect(() => {
    fetchQuizData();
  }, [quizId]);

  useEffect(() => {
    // Load saved progress when quiz loads
    loadQuizProgress();
  }, [quiz]);

  useEffect(() => {
    // Save progress when answers or current question changes
    if (quiz && Object.keys(answers).length > 0) {
      saveQuizProgress();
    }
  }, [answers, currentQuestionIndex]);

  const fetchQuizData = async () => {
    try {
      const response = await apiClient.get(`/quizzes/${quizId}`);
      setQuiz(response.data.quiz);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error('Error al cargar el cuestionario');
      navigate('/cuestionarios');
    } finally {
      setLoading(false);
    }
  };

  const loadQuizProgress = async () => {
    if (!quiz) return;
    
    try {
      const response = await apiClient.get(`/quiz-progress/${quizId}`);
      if (response.data) {
        setCurrentQuestionIndex(response.data.current_question_index);
        setAnswers(response.data.answers);
        
        // Set feedback for already answered questions
        const feedbackState = {};
        Object.keys(response.data.answers).forEach(questionId => {
          feedbackState[questionId] = true;
        });
        setShowFeedback(feedbackState);
        
        toast.info('Continuando desde donde te quedaste...');
      }
    } catch (error) {
      console.log('No previous progress found');
    }
  };

  const saveQuizProgress = async () => {
    if (!quiz) return;
    
    try {
      await apiClient.post('/quiz-progress', {
        quiz_id: quizId,
        current_question_index: currentQuestionIndex,
        answers: answers
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleAbandonQuiz = async () => {
    try {
      // Save current progress before leaving
      await saveQuizProgress();
      toast.success('Progreso guardado. Puedes continuar más tarde.');
      navigate('/cuestionarios');
    } catch (error) {
      console.error('Error saving progress:', error);
      navigate('/cuestionarios');
    }
  };

  const handleAnswerSelect = (questionId, selectedAnswer) => {
    if (showFeedback[questionId]) return; // Don't allow changes after feedback is shown
    
    setAnswers({
      ...answers,
      [questionId]: selectedAnswer
    });

    // Show feedback immediately after selection
    setShowFeedback({
      ...showFeedback,
      [questionId]: true
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Last question, show completion option
      setCompleted(true);
    }
  };

  const toggleFlashcard = async (question) => {
    const questionId = question.id;
    const isCurrentlySaved = savedFlashcards[questionId];
    
    setSavingFlashcard(questionId);
    
    // Normalize specialty to one of the 5 base categories
    const normalizeSpecialty = (specialty) => {
      const specialtyLower = (specialty || '').toLowerCase();
      if (specialtyLower.includes('ginecología') || specialtyLower.includes('obstetricia')) {
        return 'Ginecología y Obstetricia';
      } else if (specialtyLower.includes('cirugía') || specialtyLower.includes('cirugia')) {
        return 'Cirugía';
      } else if (specialtyLower.includes('pediatría') || specialtyLower.includes('pediatria')) {
        return 'Pediatría';
      } else if (specialtyLower.includes('medicina interna') || specialtyLower.includes('cardiología') || 
                 specialtyLower.includes('cardiologia') || specialtyLower.includes('endocrinología') ||
                 specialtyLower.includes('nefrología') || specialtyLower.includes('neumología') ||
                 specialtyLower.includes('gastroenterología') || specialtyLower.includes('neurología') ||
                 specialtyLower.includes('reumatología') || specialtyLower.includes('hematología') ||
                 specialtyLower.includes('infectología') || specialtyLower.includes('dermatología')) {
        return 'Medicina Interna';
      } else {
        return 'Otros';
      }
    };
    
    try {
      if (isCurrentlySaved) {
        // Remove flashcard
        await apiClient.delete(`/flashcards/by-question/${questionId}`);
        setSavedFlashcards(prev => {
          const newState = { ...prev };
          delete newState[questionId];
          return newState;
        });
        toast.success('Flashcard eliminada');
      } else {
        // Get the correct answer text
        const correctOption = `option_${question.correct_answer.toLowerCase()}`;
        const correctAnswerText = question[correctOption];
        
        // Get the subtopic from quiz topic
        const baseSpecialty = normalizeSpecialty(quiz.specialty);
        
        // Save as flashcard
        await apiClient.post('/flashcards', {
          question_id: questionId,
          quiz_id: quizId,
          quiz_title: quiz.topic,
          specialty: baseSpecialty,
          topic: quiz.topic,
          question_text: question.question_text,
          answer_text: correctAnswerText,
          explanation: question.explanation || null
        });
        setSavedFlashcards(prev => ({ ...prev, [questionId]: true }));
        toast.success('Guardada en Flash cards ✓');
      }
    } catch (error) {
      console.error('Error toggling flashcard:', error);
      if (error.response?.data?.detail?.includes('ya está guardada')) {
        setSavedFlashcards(prev => ({ ...prev, [questionId]: true }));
      } else {
        toast.error('Error al guardar flashcard');
      }
    } finally {
      setSavingFlashcard(null);
    }
  };

  const checkFlashcardStatus = async (questionId) => {
    try {
      const response = await apiClient.get(`/flashcards/check/${questionId}`);
      if (response.data.exists) {
        setSavedFlashcards(prev => ({ ...prev, [questionId]: true }));
      }
    } catch (error) {
      console.error('Error checking flashcard status:', error);
    }
  };

  // Check flashcard status when feedback is shown
  useEffect(() => {
    const validIndex = currentQuestionIndex >= questions.length ? 0 : currentQuestionIndex;
    const currentQ = questions[validIndex];
    if (currentQ && showFeedback[currentQ.id]) {
      checkFlashcardStatus(currentQ.id);
    }
  }, [showFeedback, currentQuestionIndex, questions]);

  const submitQuiz = async () => {
    // Check if all questions are answered
    const unansweredQuestions = questions.filter(q => !answers[q.id]);
    if (unansweredQuestions.length > 0) {
      toast.error(`Faltan ${unansweredQuestions.length} preguntas por responder`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiClient.post('/quiz-attempts', {
        quiz_id: quizId,
        answers: answers
      });
      
      setResults(response.data);
      checkRankUp(response.data);
      toast.success('¡Cuestionario completado!');
    } catch (error) {
      console.error('Error submitting quiz:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMessage = error.response?.data?.detail || 'Error al enviar las respuestas';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 70) return 'score-good';
    return 'score-poor';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return '¡Excelente! Dominas muy bien el tema.';
    if (score >= 70) return 'Buen trabajo, pero puedes mejorar.';
    return 'Necesitas estudiar más este tema.';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (results) {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
            <div className="mb-8">
              <Award size={64} className="mx-auto text-black mb-4" />
              <h2 className="text-3xl font-bold text-black mb-2">
                ¡Cuestionario Completado!
              </h2>
              <p className="text-gray-600">{quiz.title}</p>
            </div>

            <div className={`rounded-2xl p-8 mb-8 ${getScoreColor(results.score)}`}>
              <div data-testid="gpc-result-score" className="text-4xl font-bold mb-2">
                {results.score.toFixed(1)}%
              </div>
              <div data-testid="gpc-result-correct-count" className="text-lg">
                {results.correct_answers} de {results.total_questions} correctas
              </div>
              <div className="mt-4 text-lg">
                {getScoreMessage(results.score)}
              </div>
              {results.points_result && <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm font-semibold" role="status">
                <span data-testid="gpc-result-points">+{results.points_result.points_awarded} puntos</span>
                <span data-testid="gpc-result-total-points">Total acumulado: {results.points_result.total_points}</span>
              </div>}
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/cuestionarios"
                data-testid="return-dashboard-button"
                className="btn-secondary"
              >
                Volver a Cuestionarios
              </Link>
              <button
                data-testid="retry-quiz-button"
                className="btn-primary"
                onClick={() => {
                  // Reset quiz state to allow retry
                  setCurrentQuestionIndex(0);
                  setAnswers({});
                  setShowFeedback({});
                  setCompleted(false);
                  setResults(null);
                  // Clear any saved progress for a fresh start
                  localStorage.removeItem(`quiz_progress_${quizId}`);
                  toast.success('¡Puedes intentarlo de nuevo!');
                }}
              >
                🔄 Volver a Intentar
              </button>
              <Link
                to="/leaderboard"
                data-testid="view-leaderboard-button"
                className="btn-secondary"
              >
                Ver Ranking
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Safety checks for questions array
  if (!questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-black mb-2">No hay preguntas disponibles</h2>
          <p className="text-gray-500 mb-6">Este cuestionario no tiene preguntas o están cargando</p>
          <Link to="/cuestionarios" className="btn-primary">
            Volver al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Bounds checking for currentQuestionIndex - get valid index
  const validIndex = currentQuestionIndex >= questions.length ? 0 : currentQuestionIndex;
  const currentQuestion = questions[validIndex];
  const progress = ((validIndex + 1) / questions.length) * 100;

  // Additional safety check for currentQuestion
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-black mb-2">Error al cargar la pregunta</h2>
          <p className="text-gray-500 mb-6">No se pudo cargar la pregunta actual</p>
          <Link to="/cuestionarios" className="btn-primary">
            Volver al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            data-testid="back-to-dashboard-link"
            className="flex items-center space-x-2 text-black hover:text-gray-600 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Volver al Dashboard</span>
          </Link>
          
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">
              Pregunta {currentQuestionIndex + 1} de {questions.length}
            </span>
            <button
              data-testid="abandon-quiz-button"
              className="btn-danger text-sm px-3 py-1"
              onClick={() => setShowAbandonConfirm(true)}
            >
              Abandonar
            </button>
            <div className="text-black font-semibold">{user.full_name}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-2xl font-bold text-black">{quiz.title}</h1>
            <div className="text-sm text-gray-600">{progress.toFixed(0)}% completado</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-black h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="quiz-question animate-fadeIn">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <BookOpen size={24} className="text-black mr-2" />
              <span className="text-lg font-semibold text-gray-700">
                Pregunta {currentQuestionIndex + 1}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-black leading-relaxed">
              {currentQuestion.question_text}
            </h2>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {['A', 'B', 'C', 'D'].map((option) => {
              const optionKey = `option_${option.toLowerCase()}`;
              const optionText = currentQuestion[optionKey];
              
              // Skip if option text is missing or invalid
              if (!optionText || 
                  typeof optionText !== 'string' || 
                  optionText === 'undefined' || 
                  optionText === 'null' || 
                  optionText === 'NaN' ||
                  optionText.trim() === '') {
                console.warn(`Missing or invalid option ${option} for question ${currentQuestion.id}`);
                return null;
              }
              
              const isSelected = answers[currentQuestion.id] === option;
              const isCorrect = currentQuestion.correct_answer === option;
              const showResult = showFeedback[currentQuestion.id];
              
              let optionClass = 'quiz-option';
              if (isSelected && !showResult) {
                optionClass += ' selected';
              }
              if (showResult) {
                if (isCorrect) {
                  optionClass += ' correct';
                } else if (isSelected && !isCorrect) {
                  optionClass += ' incorrect';
                }
              }

              return (
                <div
                  key={option}
                  data-testid={`option-${option.toLowerCase()}`}
                  className={optionClass}
                  onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-semibold text-lg mr-3 text-black">
                        {option}.
                      </span>
                      <span>{optionText}</span>
                    </div>
                    {showResult && (
                      <div className="ml-4">
                        {isCorrect ? (
                          <Check size={24} className="icon-check" />
                        ) : (
                          isSelected && !isCorrect && <X size={24} className="icon-x" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Submit Button - Only show if no answer selected */}
          {!answers[currentQuestion.id] && !showFeedback[currentQuestion.id] && (
            <div className="text-center text-gray-500 py-4">
              Selecciona una respuesta para ver la retroalimentación
            </div>
          )}

          {/* Feedback */}
          {showFeedback[currentQuestion.id] && (
            <div className="feedback-box animate-fadeIn">
              <h4>
                {answers[currentQuestion.id] === currentQuestion.correct_answer ? (
                  <span className="flex items-center text-black">
                    <Check size={20} className="mr-2" />
                    ¡Correcto!
                  </span>
                ) : (
                  <span className="flex items-center text-gray-500">
                    <X size={20} className="mr-2" />
                    Incorrecto
                  </span>
                )}
              </h4>
              <p><strong>Respuesta correcta:</strong> {currentQuestion.correct_answer || 'N/A'}</p>
              {currentQuestion.explanation && 
               typeof currentQuestion.explanation === 'string' &&
               currentQuestion.explanation !== 'undefined' && 
               currentQuestion.explanation !== 'null' &&
               currentQuestion.explanation.trim() !== '' && (
                <p><strong>Explicación:</strong> {currentQuestion.explanation}</p>
              )}
              {currentQuestion.reference && 
               typeof currentQuestion.reference === 'string' &&
               currentQuestion.reference !== 'undefined' && 
               currentQuestion.reference !== 'null' &&
               currentQuestion.reference.trim() !== '' && (
                <p><strong>Referencia:</strong> {currentQuestion.reference}</p>
              )}
              
              {/* Flashcard Toggle */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => toggleFlashcard(currentQuestion)}
                  disabled={savingFlashcard === currentQuestion.id}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    savedFlashcards[currentQuestion.id]
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {savingFlashcard === currentQuestion.id ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  ) : savedFlashcards[currentQuestion.id] ? (
                    <BookmarkCheck size={20} />
                  ) : (
                    <Bookmark size={20} />
                  )}
                  <span>
                    {savedFlashcards[currentQuestion.id]
                      ? 'Guardada en Flash cards ✓'
                      : 'Agregar a Flash cards personales'}
                  </span>
                </button>
              </div>
              
              <div className="mt-4 space-x-4">
                {currentQuestionIndex < questions.length - 1 ? (
                  <button
                    data-testid="next-question-button"
                    className="btn-primary"
                    onClick={handleNextQuestion}
                  >
                    Siguiente Pregunta →
                  </button>
                ) : (
                  <button
                    data-testid="finish-quiz-button"
                    className="btn-primary"
                    onClick={submitQuiz}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="loading-spinner mr-2"></div>
                        Enviando...
                      </>
                    ) : (
                      'Finalizar Cuestionario'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Abandon Quiz Confirmation Modal */}
        {showAbandonConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md mx-4 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-4">
                ¿Abandonar Cuestionario?
              </h3>
              <p className="text-gray-600 mb-6">
                Tu progreso se guardará y podrás continuar desde donde te quedaste la próxima vez.
              </p>
              <div className="flex space-x-4">
                <button
                  data-testid="confirm-abandon-button"
                  className="btn-danger flex-1"
                  onClick={handleAbandonQuiz}
                >
                  Sí, Abandonar
                </button>
                <button
                  data-testid="cancel-abandon-button"
                  className="btn-secondary flex-1"
                  onClick={() => setShowAbandonConfirm(false)}
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Removed duplicate navigation - now handled in feedback section */}
      </div>
    </div>
  );
};

export default QuizView;
